import React from "react";
import { TextField, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/system";
import MDBox from "../../../../../components/MDBox";
import MDTypography from "../../../../../components/MDTypography";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import { Segment } from "../../types/types";
import { getOrdinal } from "../../utils/numberUtils";
import { cruxyColors } from "../../theme/color-palette";
import colors from "assets/theme-dark/base/colors";
interface ColumnRange {
  isNumeric: boolean;
  min?: number;
  max?: number;
}

interface SegmentThresholdsProps {
  segments: Segment[];
  numberOfSegments: number;
  handleMinValueChange: (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleMaxValueChange: (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSegment: () => void;
  handleRemoveSegment: (id: number) => () => void;
  handleRemoveLastSegment: () => void;
  columnRanges: Record<string, ColumnRange>;
  type: string;
}

const SegmentThresholds: React.FC<SegmentThresholdsProps> = ({
  segments,
  numberOfSegments,
  handleMinValueChange,
  handleMaxValueChange,
  handleAddSegment,
  handleRemoveSegment,
  handleRemoveLastSegment,
  columnRanges,
  type,
}) => {
  const column = type && columnRanges[type];
  const isNumeric = column?.isNumeric;

  return (
    <StyledContainer>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="body2" fontWeight="medium" color={colors.text.main}>
          Segment Thresholds
        </MDTypography>
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <Tooltip title="Decrease segment count">
            <IconButton
              size="small"
              onClick={handleRemoveLastSegment}
              disabled={numberOfSegments <= 2}
              sx={{
                padding: "4px",
                color: cruxyColors.blue.main,
                border: `1px solid ${colors.grey[300]}`,
                borderRadius: "4px",
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <MDTypography variant="body2" color={colors.text.main}>
            {numberOfSegments}
          </MDTypography>
          <Tooltip title="Increase segment count">
            <IconButton
              size="small"
              onClick={handleAddSegment}
              disabled={numberOfSegments >= 10 || !isNumeric}
              sx={{
                padding: "4px",
                color: cruxyColors.blue.main,
                border: `1px solid ${colors.grey[300]}`,
                borderRadius: "4px",
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MDBox>
      </MDBox>
      {segments?.map((segment, index) => (
        <StyledSegmentCard key={segment.id}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <MDTypography variant="caption" fontWeight="medium" color={colors.text.main}>
              Segment {index + 1}
            </MDTypography>
            <Tooltip title={`Remove ${getOrdinal(index + 1)} segment`}>
              <IconButton
                size="small"
                onClick={handleRemoveSegment(segment.id)}
                disabled={numberOfSegments <= 2}
                sx={{ padding: "2px", color: cruxyColors.red.main }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
          <MDBox display="flex" gap={1}>
            <TextField
              label="Min Value"
              type="number"
              value={segment.minValue}
              onChange={handleMinValueChange(segment.id)}
              fullWidth
              size="small"
              disabled={!isNumeric}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: colors.grey[300] },
                  "&:hover fieldset": { borderColor: cruxyColors.blue.main },
                  "& input": { color: colors.text.main, fontSize: "0.85rem", padding: "8px" },
                },
                "& .MuiInputLabel-root": { color: colors.text.main, fontSize: "0.85rem" },
              }}
            />
            <TextField
              label={
                index === segments.length - 1 ? (
                  "Max Value"
                ) : (
                  <span>
                    Max Value <StyledSpan>(Exclusive)</StyledSpan>
                  </span>
                )
              }
              type="number"
              value={segment.maxValue}
              onChange={handleMaxValueChange(segment.id)}
              fullWidth
              size="small"
              disabled={!isNumeric}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: colors.grey[300] },
                  "&:hover fieldset": { borderColor: cruxyColors.blue.main },
                  "& input": { color: colors.text.main, fontSize: "0.85rem", padding: "8px" },
                },
                "& .MuiInputLabel-root": { color: colors.text.main, fontSize: "0.85rem" },
              }}
            />
          </MDBox>
        </StyledSegmentCard>
      ))}
    </StyledContainer>
  );
};

const StyledContainer = styled(MDBox)(({ theme }) => ({
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  padding: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
}));

const StyledSegmentCard = styled(MDBox)(({ theme }) => ({
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  padding: "8px",
  marginBottom: "8px",
  "&:hover": {
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  },
}));

const StyledSpan = styled("span")({
  color: colors.grey[500],
  fontSize: "0.75rem",
});

export default SegmentThresholds;
